import React, { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import { useNotificationCounts } from "../../hooks/useNotifications";
import { useAccessiblePages } from "../../hooks/usePageAccess";
import { setV2LastAllowedPath } from "../../utils/v2Navigation";
import { buildV2YearSearch } from "../../hooks/useV2Year";
import { useV1AppRedirect } from "../../hooks/useV1AppRedirect";
import { isDemoHotel73 } from "../../utils/demoHotel73Stats";
import "../../styles/SecretPage.css";
import V2Sidebar from "./sidebar/Sidebar";
import Header from "./Header";

const getSidebarNavOrder = (lang) => [
  { key: "dashboard", label: translate("Dashboard_title", lang) },
  { key: "actions-water", label: translate("v2_sidebar_water_savings", lang) },
  { key: "actions-cleaning", label: translate("v2_sidebar_cleaning_savings", lang) },
  { key: "greenmiles", label: "GreenMiles" },
];

const pageNameMap = {
  dashboard: "dashboard-v2",
  greenmiles: "greenmiles-v2",
  "actions-list": "actions-list-v2",
  "actions-surveys": "actions-surveys-v2",
  "actions-reviews": "actions-reviews-v2",
  "actions-water": "actions-water-v2",
  "actions-cleaning": "actions-cleaning-v2",
  "rewards-list": "rewards-list-v2",
  "rewards-sales": "rewards-sales-v2",
  "rewards-donations": "rewards-donations-v2",
};

const V2Layout = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { hId, sethId, lang } = useContext(HotelContext);
  const [selectedHotelName, setSelectedHotelName] = React.useState(null);

  useV1AppRedirect();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const { countsByPage } = useNotificationCounts(hId);
  const { pages: accessiblePages, loading: accessiblePagesLoading } = useAccessiblePages(hId);

  const safeCountsByPage = React.useMemo(() => countsByPage || {}, [countsByPage]);

  const accessiblePageNames = React.useMemo(() => {
    return new Set(accessiblePages.map((page) => page.pName));
  }, [accessiblePages]);

  React.useEffect(() => {
    if (!hId || accessiblePagesLoading) {
      return;
    }
    const segment = location.pathname.split("/").pop() || "dashboard";
    const pageName = pageNameMap[segment];
    if (pageName && accessiblePageNames.has(pageName)) {
      setV2LastAllowedPath(location.pathname, location.search);
    }
  }, [hId, accessiblePagesLoading, accessiblePageNames, location.pathname, location.search]);

  React.useEffect(() => {
    if (!hId || !auth.isAuthenticated || !auth.user?.access_token) {
      setSelectedHotelName(null);
      return;
    }

    if (isDemoHotel73(hId)) {
      setSelectedHotelName("Hôtel Démo");
      return;
    }

    fetch("/api/groups/hotels", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + auth.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === "OK") {
          const independentHotels = (data.independent || []).map((hotel) => ({
            id: hotel.hId,
            name: hotel.name || hotel.hName || `Hôtel ${hotel.hId}`,
          }));

          const groupHotels = (data.groups || []).flatMap((group) =>
            (group.hotels || []).map((hotel) => ({
              id: hotel.hId || hotel.id,
              name: hotel.name || hotel.hName || `Hôtel ${hotel.hId || hotel.id}`,
            }))
          );

          const allHotels = [...independentHotels, ...groupHotels];
          const hotel = allHotels.find((h) => h.id === parseInt(hId, 10));
          setSelectedHotelName(hotel?.name || translate("v2_header_hotel_fallback", lang, { id: hId }));
        }
      })
      .catch((err) => {
        console.error("Error fetching hotel name:", err);
        setSelectedHotelName(translate("v2_header_hotel_fallback", lang, { id: hId }));
      });
  }, [hId, auth.isAuthenticated, auth.user?.access_token, lang]);

  const activePage = location.pathname.split("/").pop() || "dashboard";

  const sidebarItems = useMemo(() => {
    const getBadge = (pageKey) => {
      const pageName = pageNameMap[pageKey];
      const count = safeCountsByPage[pageName];
      if (count !== undefined && count !== null && count > 0) {
        return count;
      }
      return undefined;
    };

    if (!hId || accessiblePagesLoading) {
      return [];
    }

    return getSidebarNavOrder(lang).filter((item) => {
      const pageName = pageNameMap[item.key];
      return accessiblePageNames.has(pageName);
    }).map((item) => ({
      ...item,
      badge: getBadge(item.key),
    }));
  }, [safeCountsByPage, accessiblePageNames, accessiblePagesLoading, hId, lang]);

  const handlePageSelect = (pageKey) => {
    const basePath = "/hotel/lunishower/v2";
    const from = `${location.pathname}${location.search}`;
    const params = new URLSearchParams(location.search);
    const year = params.get("year") || String(new Date().getFullYear());
    navigate(`${basePath}/${pageKey}${buildV2YearSearch(location.search, year)}`, {
      state: { from },
    });
  };

  const sidebarReady = Boolean(hId) && !accessiblePagesLoading;

  return (
    <div className="secret-v2">
      <V2Sidebar
        items={sidebarItems}
        loading={!sidebarReady}
        activeKey={activePage}
        onSelect={handlePageSelect}
        userProfile={{
          name:
            auth.user?.profile?.name ??
            `${auth.user?.profile?.given_name ?? "Luni"} ${auth.user?.profile?.family_name ?? "Manager"}`,
          picture: auth.user?.profile?.picture,
        }}
      />

      <main className="secret-v2__content">
        <Header
          hotelName={selectedHotelName}
          hId={hId}
          onMesHotelsClick={() => {
            localStorage.removeItem("hId");
            localStorage.removeItem("access");
            sessionStorage.setItem("resetHotelId", "true");
            sethId(null);
            window.location.href = "/user/hotels";
          }}
        />
        <section className="secret-v2__main-content">
          <Outlet />
        </section>
      </main>
    </div>
  );
};

export default V2Layout;
